import React, { useCallback, useId, useRef, useState } from 'react';
import type { TooltipPosition, TooltipProps } from './types';

// ─── Position class maps ──────────────────────────────────────────────────────

const POSITION_CLASSES: Record<
  TooltipPosition,
  { tooltip: string; arrow: string }
> = {
  top: {
    tooltip: 'bottom-full left-1/2 -translate-x-1/2 mb-2',
    arrow: 'top-full left-1/2 -translate-x-1/2 border-l-transparent border-r-transparent border-b-transparent border-t-neutral-900',
  },
  bottom: {
    tooltip: 'top-full left-1/2 -translate-x-1/2 mt-2',
    arrow: 'bottom-full left-1/2 -translate-x-1/2 border-l-transparent border-r-transparent border-t-transparent border-b-neutral-900',
  },
  left: {
    tooltip: 'right-full top-1/2 -translate-y-1/2 mr-2',
    arrow: 'left-full top-1/2 -translate-y-1/2 border-t-transparent border-b-transparent border-r-transparent border-l-neutral-900',
  },
  right: {
    tooltip: 'left-full top-1/2 -translate-y-1/2 ml-2',
    arrow: 'right-full top-1/2 -translate-y-1/2 border-t-transparent border-b-transparent border-l-transparent border-r-neutral-900',
  },
};

// ─── Tooltip ──────────────────────────────────────────────────────────────────

export function Tooltip({
  content,
  children,
  position = 'top',
  delay = 300,
  maxWidth = 240,
  className,
}: TooltipProps) {
  const [visible, setVisible] = useState(false);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const tooltipId = useId();
  const p = POSITION_CLASSES[position];

  const show = useCallback(() => {
    timerRef.current = setTimeout(() => setVisible(true), delay);
  }, [delay]);

  const hide = useCallback(() => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
    setVisible(false);
  }, []);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'Escape') hide();
    },
    [hide],
  );

  return (
    <span
      className="relative inline-flex"
      onMouseEnter={show}
      onMouseLeave={hide}
      onFocus={show}
      onBlur={hide}
      onKeyDown={handleKeyDown}
      aria-describedby={visible ? tooltipId : undefined}
    >
      {children}

      {visible && (
        <span
          id={tooltipId}
          role="tooltip"
          className={[
            'pointer-events-none absolute z-[9999] whitespace-normal break-words',
            'rounded-md bg-neutral-900 px-2.5 py-1.5 text-xs text-white shadow-md',
            'animate-fade-in',
            p.tooltip,
            className ?? '',
          ]
            .filter(Boolean)
            .join(' ')}
          style={{ maxWidth }}
        >
          {content}
          {/* Arrow */}
          <span
            className={[
              'absolute h-0 w-0 border-4',
              p.arrow,
            ].join(' ')}
            aria-hidden="true"
          />
        </span>
      )}
    </span>
  );
}
